import { useNavigate, createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Calendar,
  FormField,
  Input,
  TimeSlotPicker,
  Textarea,
  type TimeSlot,
} from '@bookaburra/ui';
import { ArrowLeft, XCircle } from 'lucide-react';
import {
  createBooking,
  fetchAvailability,
  fetchBookingTenant,
  fetchServices,
  fetchSlots,
  joinWaitlist,
  type ApiBookingError,
} from '../booking-flow/api';
import { TurnstileWidget } from '../components/TurnstileWidget';
import {
  AddressAutocomplete,
  type PickedAddress,
} from '../components/AddressAutocomplete';
import { useDocumentTitle } from '../lib/use-document-title';
import { CheckCircle2, Home } from 'lucide-react';

interface ServiceBookSearch {
  /** Optional prefilled slot start (epoch ms) — set by the chat
   *  assistant when handing off, so the page lands on the chosen day
   *  and pre-selects the slot. */
  start?: number;
  /** Carried through from the service-list page so reload keeps the
   *  chosen translation. Not consumed here; surfaced on the API call
   *  via the parent layout's locale state when added. */
  locale?: string;
}

export const Route = createFileRoute('/book/$slug/$serviceId')({
  validateSearch: (search: Record<string, unknown>): ServiceBookSearch => ({
    start: typeof search.start === 'number'
      ? search.start
      : typeof search.start === 'string' && /^\d+$/.test(search.start)
        ? Number(search.start)
        : undefined,
    locale: typeof search.locale === 'string' ? search.locale : undefined,
  }),
  component: ServiceBookPage,
});

function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function ServiceBookPage() {
  const { slug, serviceId } = Route.useParams();
  const { start: prefilledStart, locale: activeLocale } = Route.useSearch();
  const navigate = useNavigate();

  const [date, setDate] = useState<Date | null>(
    prefilledStart ? new Date(prefilledStart) : null,
  );
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(
    prefilledStart ? { start: prefilledStart } : null,
  );
  const [prefillApplied, setPrefillApplied] = useState<boolean>(!prefilledStart);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  // Captured for on-site services only. Snapshotted on the booking row
  // so the historical "where staff went" is immutable; also written
  // back to the client's default address for next time.
  const [serviceAddress, setServiceAddress] = useState<PickedAddress | null>(null);
  const [accessNotes, setAccessNotes] = useState<string>('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [submitErr, setSubmitErr] = useState<ApiBookingError | null>(null);

  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  const tenantTimezone = tenantQ.data?.tenant.timezone ?? 'Australia/Sydney';
  const closedDays = useMemo(
    () => tenantQ.data?.closedDays ?? [],
    [tenantQ.data],
  );
  const closedByDate = useMemo(
    () => new Map(closedDays.map((c) => [c.date, c.note])),
    [closedDays],
  );

  type WeeklyHoursMap = Record<
    'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun',
    { open: boolean; openMinute?: number; closeMinute?: number }
  >;
  /** Parsed weekly business-hours map keyed by short weekday code
   *  (`mon`..`sun`). `null` when the tenant hasn't configured hours
   *  yet — in which case only explicit closed_days overrides disable
   *  dates. Malformed JSON degrades to `null` too. */
  const businessHoursByDow = useMemo<WeeklyHoursMap | null>(() => {
    const raw = tenantQ.data?.tenant.businessHours;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [tenantQ.data]);

  const servicesQ = useQuery({
    queryKey: ['services', slug],
    queryFn: () => fetchServices(slug),
  });
  const service = servicesQ.data?.services.find((s) => s.id === serviceId);

  /** Per-service override (same shape as tenant hours). When set, it
   *  fully replaces tenant hours for closure decisions — matches the
   *  slots endpoint's `state.businessHours = override ?? tenantHours`
   *  swap. Null = inherit tenant hours. */
  const serviceOverrideByDow = useMemo<WeeklyHoursMap | null>(() => {
    const raw = service?.businessHoursOverride;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [service]);

  const effectiveHoursByDow = serviceOverrideByDow ?? businessHoursByDow;
  /** True when the tenant hasn't published any opening hours yet.
   *  Booking POST is server-side refused; we short-circuit the
   *  whole page so the form / calendar / submit button never
   *  render. Deep-links from the chat handoff also land here. */
  const hoursMissing =
    !tenantQ.isLoading && !tenantQ.data?.tenant.businessHours;

  /** Disable predicate for the calendar. A date is closed when either:
   *  1. It appears in the tenant's `closed_days` overrides (a one-off
   *     closure like a public holiday — keyed by YYYY-MM-DD in the
   *     tenant's wall-clock zone), OR
   *  2. The effective weekly hours (service override if present, else
   *     tenant hours) mark that day-of-week as `open: false`.
   *  Uses the same YYYY-MM-DD derivation as `toLocalDateString` so it
   *  lines up with the server allocator's wall-clock check. */
  const isDateClosed = useCallback(
    (d: Date): boolean => {
      if (closedByDate.has(toLocalDateString(d))) return true;
      if (effectiveHoursByDow) {
        const dowKeys: ReadonlyArray<
          'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat'
        > = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        const key = dowKeys[d.getDay()];
        if (key) {
          const hours = effectiveHoursByDow[key];
          if (hours && hours.open === false) return true;
        }
      }
      return false;
    },
    [closedByDate, effectiveHoursByDow],
  );

  // If the picked date later turns out to be closed (e.g. closed days
  // load after the user clicked a prefilled slot, or the service
  // override loads after the tenant data), drop the selection.
  // Uses the local-only predicate here; the server-authoritative
  // version isn't ready yet on first render and we don't want to
  // drop a prefilled slot just because availability hasn't loaded.
  useEffect(() => {
    if (date && isDateClosed(date)) {
      setDate(null);
      setSelectedSlot(null);
    }
  }, [date, isDateClosed]);
  const isOnSite = service?.locationType === 'on_site';
  // Postcode whitelist check — empty list means "no restriction".
  // The check runs *after* the client has picked a Mapbox address;
  // free-text fallback addresses (no postcode) fail this check and
  // surface the same "we don't cover your area" error, which is
  // technically not always true but is safer than guessing.
  const whitelist = tenantQ.data?.tenant.serviceablePostcodes ?? [];
  const hasWhitelist = whitelist.length > 0;
  const pickedPostcode = serviceAddress?.postcode ?? null;
  const postcodeRejected =
    isOnSite &&
    hasWhitelist &&
    serviceAddress !== null &&
    (!pickedPostcode || !whitelist.includes(pickedPostcode));
  // On-site services need a *valid* address before we'll show the
  // calendar — travel time and postcode-whitelist checks both depend
  // on it.
  const addressReady =
    !isOnSite ||
    ((serviceAddress?.formatted ?? '').length > 0 && !postcodeRejected);

  useDocumentTitle(
    service && tenantQ.data
      ? `${service.name} · ${tenantQ.data.tenant.name}`
      : null,
  );

  const queryClient = useQueryClient();
  const dateStr = useMemo(() => (date ? toLocalDateString(date) : null), [date]);
  const slotsQ = useQuery({
    enabled: Boolean(dateStr),
    queryKey: ['slots', slug, serviceId, dateStr],
    queryFn: () => fetchSlots(slug, serviceId, dateStr!),
  });

  /** Currently-visible date-picker month. Used to bound the availability
   *  query so we only run allocator passes for dates the user can see. */
  const [pickerMonth, setPickerMonth] = useState<Date>(() => {
    const seed = date ?? new Date();
    return new Date(seed.getFullYear(), seed.getMonth(), 1);
  });
  const availabilityWindow = useMemo(() => {
    // From the first of the visible month (or today, whichever is
    // later — no point checking the past) through the end of the month.
    // Single-month window stays comfortably under the server's 35-day
    // cap and keeps the allocator work proportional to what the user
    // actually sees.
    const monthStart = new Date(pickerMonth.getFullYear(), pickerMonth.getMonth(), 1);
    const today0 = new Date();
    today0.setHours(0, 0, 0, 0);
    const from = monthStart.getTime() < today0.getTime() ? today0 : monthStart;
    const monthEnd = new Date(pickerMonth.getFullYear(), pickerMonth.getMonth() + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);
    return { from: from.getTime(), to: monthEnd.getTime() };
  }, [pickerMonth]);

  const availabilityQ = useQuery({
    enabled: Boolean(service) && addressReady,
    queryKey: [
      'availability',
      slug,
      serviceId,
      availabilityWindow.from,
      availabilityWindow.to,
    ],
    queryFn: () =>
      fetchAvailability(
        slug,
        serviceId,
        availabilityWindow.from,
        availabilityWindow.to,
      ),
    staleTime: 30_000,
  });

  /** Map of YYYY-MM-DD → hasSlots for fast lookup in the modifier. */
  const hasSlotsByDate = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const d of availabilityQ.data?.days ?? []) m.set(d.date, d.hasSlots);
    return m;
  }, [availabilityQ.data]);

  /** Server-authoritative closed-day set. Catches cases the local
   *  `isDateClosed` predicate misses — e.g. tenants whose
   *  `businessHours` JSON wasn't parseable client-side, or where the
   *  service override hasn't yet been threaded into the client cache.
   *  The server runs the same dow lookup against the effective
   *  weekly hours; we trust its answer. */
  const closedFromServer = useMemo(() => {
    const s = new Set<string>();
    for (const d of availabilityQ.data?.days ?? []) {
      if (d.closed) s.add(d.date);
    }
    return s;
  }, [availabilityQ.data]);

  /** Final closed-day predicate — union of the local check and the
   *  server's per-day `closed` flag. Either source can mark a day
   *  closed; both have to agree it's open before we treat it that
   *  way. */
  const isDateClosedFinal = useCallback(
    (d: Date): boolean =>
      isDateClosed(d) || closedFromServer.has(toLocalDateString(d)),
    [isDateClosed, closedFromServer],
  );

  /** True if the availability endpoint marked the day as fully booked
   *  (hasSlots === false) AND the day isn't already closed. Closed days
   *  win — we only want one disabled/red treatment per cell. */
  const isFullyBooked = useCallback(
    (d: Date): boolean => {
      if (isDateClosedFinal(d)) return false;
      const key = toLocalDateString(d);
      if (!hasSlotsByDate.has(key)) return false;
      return hasSlotsByDate.get(key) === false;
    },
    [isDateClosedFinal, hasSlotsByDate],
  );

  /** Find the soonest non-closed, non-fully-booked date strictly after
   *  `from`. Bounded by the loaded availability window so we never
   *  recommend a date the server hasn't classified. Returns null when
   *  every visible day is booked or closed. */
  function findNextAvailableDate(from: Date): Date | null {
    const sorted = [...hasSlotsByDate.entries()]
      .filter(([, has]) => has)
      .map(([iso]) => iso)
      .sort();
    const fromIso = toLocalDateString(from);
    for (const iso of sorted) {
      if (iso <= fromIso) continue;
      const [y, m, day] = iso.split('-').map(Number);
      if (!y || !m || !day) continue;
      const d = new Date(y, m - 1, day);
      if (!isDateClosedFinal(d)) return d;
    }
    return null;
  }

  // When the chat assistant hands off via `?start=`, verify the slot
  // still exists once the day's slots load. If it's been taken since
  // (or never existed), drop the pre-selection so the picker stops
  // showing a phantom highlight.
  useEffect(() => {
    if (prefillApplied) return;
    if (!slotsQ.data) return;
    const exists = slotsQ.data.slots.some((s) => s.start === prefilledStart);
    if (!exists) setSelectedSlot(null);
    setPrefillApplied(true);
  }, [slotsQ.data, prefillApplied, prefilledStart]);

  const bookMut = useMutation({
    mutationFn: createBooking,
    onSuccess: (data) => {
      // The just-booked slot might've been the last one on its day —
      // invalidate availability so a return to the picker reflects it.
      queryClient.invalidateQueries({
        queryKey: ['availability', slug, serviceId],
      });
      navigate({
        to: '/book/$slug/booked/$bookingId',
        params: { slug, bookingId: data.booking.id },
        search: { manageToken: data.manageToken },
      });
    },
    onError: (err: Error & ApiBookingError) => setSubmitErr(err),
  });

  const validEmail = !email || /.+@.+\..+/.test(email);
  const validPhone = !phone || /[0-9+ -]{7,}/.test(phone);
  const hasContact = email.trim() || phone.trim();
  const canSubmit =
    selectedSlot &&
    name.trim() &&
    hasContact &&
    validEmail &&
    validPhone &&
    turnstileToken &&
    !bookMut.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedSlot || !canSubmit) return;
    setSubmitErr(null);
    bookMut.mutate({
      slug,
      input: {
        serviceId,
        start: selectedSlot.start,
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        notes: notes.trim() || undefined,
        marketingConsent,
        // On-site services snapshot the client's address on the booking
        // row + write it back to the client default for next time. The
        // server ignores these fields for non-on_site services.
        serviceAddress: isOnSite && serviceAddress ? serviceAddress : undefined,
        serviceAddressAccessNotes: isOnSite ? accessNotes.trim() || undefined : undefined,
        // Record the locale the client was using so staff see a
        // language hint on the calendar (translator decisions).
        locale: activeLocale ?? tenantQ.data?.tenant.defaultLocale,
        turnstileToken: turnstileToken!,
      },
    });
  }

  const slots: TimeSlot[] = (slotsQ.data?.slots ?? []).map((s) => ({
    start: s.start,
    label: undefined,
  }));

  return (
    <main className="mx-auto max-w-5xl px-6 py-12 sm:px-12">
      <Link
        to="/book/$slug"
        params={{ slug }}
        className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> All services
      </Link>

      <header className="mt-6 mb-10">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">Book online</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-5xl">
          {service?.name ?? 'Loading…'}
        </h1>
        <p className="mt-3 text-base text-muted-foreground">
          {service ? `${service.durationMinutes} minutes` : null}
        </p>
      </header>

      {hoursMissing ? (
        <Alert className="mb-10 border-amber-500/40 bg-amber-50 text-amber-900">
          <AlertTitle>Bookings paused</AlertTitle>
          <AlertDescription>
            {tenantQ.data?.tenant.name ?? 'This business'} is still being
            set up — their opening hours haven&rsquo;t been published yet,
            so we&rsquo;re not accepting bookings on their behalf. Try
            again later, or contact them directly.
          </AlertDescription>
        </Alert>
      ) : null}

      {!hoursMissing ? (
      <>


      {isOnSite ? (
        <section className="mb-10 rounded-2xl border border-brand-sage/15 bg-card p-6 shadow-brand sm:p-8">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            <Home className="h-4 w-4 text-brand-sage" /> Where should we come?
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            We&rsquo;ll come to your place. Pick your address before
            choosing a time so we can check we cover your area.
          </p>
          <FormField label="Address" required>
            {({ id, invalid, describedBy }) => (
              <AddressAutocomplete
                id={id}
                value={serviceAddress?.formatted ?? ''}
                onPick={setServiceAddress}
                onClear={() => setServiceAddress(null)}
                invalid={invalid}
                describedBy={describedBy}
                country={tenantQ.data?.tenant.country ?? 'AU'}
                placeholder="Start typing your address…"
              />
            )}
          </FormField>
          {postcodeRejected ? (
            <Alert variant="destructive" className="mt-4">
              <XCircle className="h-4 w-4" />
              <AlertTitle>
                We don&rsquo;t cover this area yet
              </AlertTitle>
              <AlertDescription>
                {tenantQ.data?.tenant.name ?? 'This business'}{' '}
                doesn&rsquo;t currently service{' '}
                {pickedPostcode ? (
                  <strong>postcode {pickedPostcode}</strong>
                ) : (
                  <>this address</>
                )}
                . Try a different address or get in touch with the
                business to ask about coverage.
              </AlertDescription>
            </Alert>
          ) : null}
          <FormField
            label="Access notes"
            description="Optional. e.g. side gate, dog out the back, parking around the corner."
          >
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                rows={2}
                value={accessNotes}
                onChange={(e) => setAccessNotes(e.target.value)}
                maxLength={500}
              />
            )}
          </FormField>
        </section>
      ) : null}

      {!addressReady && !postcodeRejected ? (
        <p className="mb-10 rounded-md border border-dashed border-border bg-secondary/40 px-5 py-6 text-sm text-muted-foreground">
          Pick your address above to see available times.
        </p>
      ) : null}

      <div
        className={`grid gap-12 lg:grid-cols-[auto_1fr] lg:gap-16 ${
          !addressReady ? 'pointer-events-none opacity-40' : ''
        }`}
      >
        <div>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Pick a day
          </h2>
          <Calendar
            value={date}
            onChange={(d) => {
              setDate(d);
              setSelectedSlot(null);
            }}
            minDate={new Date()}
            isDateDisabled={isDateClosedFinal}
            onMonthChange={setPickerMonth}
            dateModifier={(d) =>
              isFullyBooked(d)
                ? {
                    // Red text + faint red tint, still clickable so the
                    // user can land on the day's empty state and read
                    // why nothing's available. Match the destructive
                    // theme token so it picks up brand-red automatically.
                    className:
                      'text-destructive bg-destructive/5 hover:bg-destructive/10 cursor-pointer',
                    ariaLabelSuffix: 'fully booked',
                  }
                : null
            }
          />
          {closedDays.length > 0 ? (
            <p className="mt-3 max-w-sm text-xs text-muted-foreground">
              Greyed-out dates are closed (public holidays or other closures).
            </p>
          ) : null}
          {[...hasSlotsByDate.values()].some((v) => v === false) ? (
            <p className="mt-2 max-w-sm text-xs text-muted-foreground">
              Dates shown in <span className="text-destructive">red</span> are fully booked.
            </p>
          ) : null}
        </div>

        <div>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Pick a time
          </h2>
          {!date ? (
            <p className="rounded-md border border-dashed border-border bg-secondary/50 p-6 text-sm text-muted-foreground">
              Choose a day on the left.
            </p>
          ) : slotsQ.isLoading ? (
            <SlotSkeleton />
          ) : slots.length === 0 &&
            (isFullyBooked(date) || isDateClosedFinal(date)) ? (
            <FullyBookedEmptyState
              date={date}
              reason={isDateClosedFinal(date) ? 'closed' : 'full'}
              onPickNext={() => {
                const next = findNextAvailableDate(date);
                if (next) {
                  setDate(next);
                  setSelectedSlot(null);
                  setPickerMonth(
                    new Date(next.getFullYear(), next.getMonth(), 1),
                  );
                }
              }}
              nextAvailable={findNextAvailableDate(date)}
            />
          ) : (
            <TimeSlotPicker
              slots={slots}
              value={selectedSlot?.start ?? null}
              onSelect={(s) => setSelectedSlot(s)}
              timezone={tenantTimezone}
              emptyMessage="No times available on this day."
            />
          )}

          {date &&
          !slotsQ.isLoading &&
          slots.length === 0 &&
          tenantQ.data?.tenant.waitlistEnabled ? (
            <div className="mt-6">
              <WaitlistJoinPanel
                slug={slug}
                serviceId={serviceId}
                dateLocal={dateStr!}
                dateLabel={
                  date
                    ? date.toLocaleDateString('en-AU', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                      })
                    : ''
                }
                serviceName={service?.name ?? 'this service'}
                locale={activeLocale ?? tenantQ.data?.tenant.defaultLocale}
                country={tenantQ.data?.tenant.country ?? 'AU'}
              />
            </div>
          ) : null}
        </div>
      </div>

      {selectedSlot ? (
        <form onSubmit={onSubmit} className="mt-16 grid gap-5 rounded-2xl border border-brand-sage/15 bg-card p-8 shadow-brand lg:max-w-2xl">
          <h2 className="text-xl font-semibold">Your details</h2>
          <FormField label="Your name" required>
            {({ id, invalid, describedBy }) => (
              <Input
                id={id}
                invalid={invalid}
                aria-describedby={describedBy}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sarah Mitchell"
                autoComplete="name"
              />
            )}
          </FormField>
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              label="Email"
              error={email && !validEmail ? 'Enter a valid email.' : undefined}
              description="Either email or phone is required."
            >
              {({ id, invalid, describedBy }) => (
                <Input
                  id={id}
                  type="email"
                  invalid={invalid}
                  aria-describedby={describedBy}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="sarah@example.com.au"
                  autoComplete="email"
                />
              )}
            </FormField>
            <FormField
              label="Phone"
              error={phone && !validPhone ? 'Enter a valid phone.' : undefined}
            >
              {({ id, invalid, describedBy }) => (
                <Input
                  id={id}
                  type="tel"
                  invalid={invalid}
                  aria-describedby={describedBy}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="0400 000 000"
                  autoComplete="tel"
                />
              )}
            </FormField>
          </div>
          {tenantQ.data?.tenant.hasSms === false &&
          phone.trim() &&
          !email.trim() ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <span className="font-medium">Heads up:</span>{' '}
              {tenantQ.data?.tenant.name ?? 'This business'} doesn&rsquo;t send
              SMS yet, so booking confirmations and reminders go by email only.
              Add an email address to make sure you receive them.
            </div>
          ) : null}
          <FormField label="Notes for the team" description="Optional.">
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            )}
          </FormField>

          <label className="flex items-start gap-3 rounded-md border border-brand-sage/15 bg-secondary/40 px-4 py-3 text-sm">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-input"
            />
            <span className="text-muted-foreground">
              Yes, I&rsquo;d like{' '}
              <span className="font-medium text-foreground">
                {tenantQ.data?.tenant.name ?? 'this business'}
              </span>{' '}
              to contact me about future appointments and offers.
              <span className="mt-1 block text-xs">
                You&rsquo;ll still receive booking confirmations and reminders
                regardless of this choice.
              </span>
            </span>
          </label>

          <TurnstileWidget action="booking" onToken={setTurnstileToken} />

          {submitErr ? (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertTitle>
                {submitErr.error === 'slot_unavailable'
                  ? 'That slot was just taken'
                  : submitErr.error === 'turnstile_failed'
                    ? 'Please complete the verification'
                    : submitErr.error === 'client_eligibility_new_only'
                      ? 'This service is for first-time clients only'
                      : submitErr.error === 'client_eligibility_returning_only'
                        ? 'This service is for existing clients only'
                        : 'Could not book'}
              </AlertTitle>
              <AlertDescription>
                {submitErr.error === 'slot_unavailable'
                  ? 'Pick another time below.'
                  : submitErr.error === 'turnstile_failed'
                    ? 'The bot-check expired or failed — please try again.'
                    : submitErr.error === 'client_eligibility_new_only'
                      ? 'Our records show a previous booking under this email or phone. Please pick a different service or contact the business.'
                      : submitErr.error === 'client_eligibility_returning_only'
                        ? 'This service is only available to existing clients. Please pick a different service or contact the business.'
                        : submitErr.error}
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex items-center justify-end gap-3">
            <Button type="submit" disabled={!canSubmit} loading={bookMut.isPending}>
              Confirm booking
            </Button>
          </div>
        </form>
      ) : null}
      </>
      ) : null}
    </main>
  );
}

function SlotSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="h-12 animate-pulse rounded-md bg-secondary" />
      ))}
    </div>
  );
}

function FullyBookedEmptyState({
  date,
  onPickNext,
  nextAvailable,
  reason = 'full',
}: {
  date: Date;
  onPickNext: () => void;
  nextAvailable: Date | null;
  /** 'closed' = business doesn't open this day-of-week.
   *  'full'   = open, but every slot is booked. */
  reason?: 'closed' | 'full';
}) {
  const dateLabel = date.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const weekday = date.toLocaleDateString('en-AU', { weekday: 'long' });
  return (
    <div className="rounded-2xl border border-brand-sage/15 bg-card p-6 shadow-brand">
      <h3 className="text-lg font-semibold">
        {reason === 'closed'
          ? `Closed on ${weekday}s`
          : `No availability on ${dateLabel}`}
      </h3>
      <p className="mt-2 text-sm text-muted-foreground">
        {reason === 'closed'
          ? `This business isn't open on ${weekday}s. Pick another day from the calendar — closed days are greyed out.`
          : 'Every slot is booked for this service that day. Try another day — fully-booked days are marked in red.'}
      </p>
      {nextAvailable ? (
        <button
          type="button"
          onClick={onPickNext}
          className="mt-3 inline-flex items-center text-sm font-medium text-primary hover:underline"
        >
          Show next available day
        </button>
      ) : null}
    </div>
  );
}

function WaitlistJoinPanel({
  slug,
  serviceId,
  dateLocal,
  dateLabel,
  serviceName,
  locale,
}: {
  slug: string;
  serviceId: string;
  dateLocal: string;
  dateLabel: string;
  serviceName: string;
  locale?: string;
  country: string;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  const joinMut = useMutation({
    mutationFn: () =>
      joinWaitlist({
        slug,
        input: {
          serviceId,
          dates: [dateLocal],
          name: name.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          smsOptIn,
          locale,
          turnstileToken: turnstileToken!,
        },
      }),
    onError: (err: Error) => setSubmitErr(err.message),
  });

  if (joinMut.data && joinMut.data.joined.length > 0) {
    return (
      <Alert variant="success">
        <CheckCircle2 className="h-4 w-4" />
        <AlertTitle>You&rsquo;re on the waiting list</AlertTitle>
        <AlertDescription>
          We&rsquo;ll email you the moment a {serviceName} slot opens on
          {' '}
          {dateLabel}. First come, first served — every person on the list
          gets the same email at the same time.
        </AlertDescription>
      </Alert>
    );
  }

  if (!open) {
    return (
      <div className="rounded-2xl border border-brand-sage/15 bg-card p-6 shadow-brand">
        <p className="text-sm font-medium">Want this day if a slot opens?</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Join the waiting list and we&rsquo;ll email you if anyone
          cancels.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => setOpen(true)}
        >
          Join the waiting list
        </Button>
      </div>
    );
  }

  const validEmail = !email || /.+@.+\..+/.test(email);
  const validPhone = !phone || /[0-9+ -]{7,}/.test(phone);
  const hasContact = email.trim() || phone.trim();
  const canSubmit =
    name.trim() &&
    hasContact &&
    validEmail &&
    validPhone &&
    turnstileToken &&
    !joinMut.isPending;

  return (
    <form
      className="grid gap-4 rounded-2xl border border-brand-sage/15 bg-card p-6 shadow-brand"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitErr(null);
        joinMut.mutate();
      }}
    >
      <div>
        <p className="text-sm font-semibold">Join the waiting list</p>
        <p className="mt-1 text-xs text-muted-foreground">
          We&rsquo;ll email you if a {serviceName} slot opens on
          {' '}
          {dateLabel}.
        </p>
      </div>
      <FormField label="Your name" required>
        {({ id }) => (
          <Input
            id={id}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sarah Mitchell"
            autoComplete="name"
          />
        )}
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Email">
          {({ id }) => (
            <Input
              id={id}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
          )}
        </FormField>
        <FormField label="Phone">
          {({ id }) => (
            <Input
              id={id}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0400 000 000"
              autoComplete="tel"
            />
          )}
        </FormField>
      </div>
      {phone ? (
        <label className="flex items-start gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={smsOptIn}
            onChange={(e) => setSmsOptIn(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-input"
          />
          <span>
            Text me if a slot opens (in addition to email).
          </span>
        </label>
      ) : null}
      <TurnstileWidget action="waitlist_join" onToken={setTurnstileToken} />
      {submitErr ? (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Couldn&rsquo;t join</AlertTitle>
          <AlertDescription>{submitErr}</AlertDescription>
        </Alert>
      ) : null}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={!canSubmit} loading={joinMut.isPending}>
          Add me to the list
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => setOpen(false)}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
